import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import Attachment, {
  AttachmentMetaData,
} from "src/app/Task/Attachments/Attachment";
import { AttachmentPlacement, TaskType } from "src/types";
import { clearAIChatContext } from "src/redux/ai-chat/aiChatActions";
import { RootState } from "src/redux/reducers";
import SelectedContextBadge from "../SelectedContextBadge/SelectedContextBadge";

import "./ChatInputField.scss";

interface ChatInputFieldProps {
  files: File[];
  onRemoveFile: (index: number) => void;
  children?: React.ReactNode;
}

export default function ChatInputField({
  files,
  onRemoveFile,
  children,
}: ChatInputFieldProps) {
  const dispatch = useDispatch();
  const wsMemberSelectedContext = useSelector(
    (state: RootState) => state.aiChatReducer.wsMemberSelectedContext,
  );

  const attachments = useMemo(
    () =>
      files.map((file) => {
        const isImage = file.type.startsWith("image/");
        const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
        const data: AttachmentMetaData = {
          name: file.name,
          mimeType: file.type || null,
          publicUrl: previewUrl,
          thumbnails: previewUrl ? { small: previewUrl } : undefined,
        };
        return { file, data, previewUrl };
      }),
    [files],
  );

  useEffect(
    () => () => {
      attachments.forEach(({ previewUrl }) => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
      });
    },
    [attachments],
  );

  return (
    <div className="chat-input-field">
      {children}
      {(attachments.length > 0 || wsMemberSelectedContext) && (
        <div className="chat-input-field__files">
          {attachments.map(({ file, data }, index) => (
            <Attachment
              key={`${file.name}-${index}`}
              data={data}
              place={AttachmentPlacement.AttachmentPreview}
              taskType={TaskType.Creator}
              handleRemoveAttachment={() => onRemoveFile(index)}
              hideDragAndDropIcon
            />
          ))}
          {wsMemberSelectedContext && (
            <SelectedContextBadge
              selectedContext={wsMemberSelectedContext}
              onRemove={() => dispatch(clearAIChatContext())}
            />
          )}
        </div>
      )}
    </div>
  );
}
